import { fetcher } from '@commenda/utils';
import {
  AM_TP_DEMO_ROUTES,
  TAmDemoAiContextFor,
  TAmDemoAiInvokeResponseFor,
  TAmDemoAiTask,
  TAmDemoResult,
} from './types';

/**
 * The single transport behind all eight demo AI tasks.
 *
 * There is one endpoint underneath because the tasks differ only in fixture and payload, but every call site
 * gets a fully narrowed result: the generic is pinned by the literal each wrapper passes, so
 * `far.extract` cannot be paired with another task's context and its result is never a union.
 *
 * Prefer the per-task wrappers in this folder over calling this directly — they are what keep the task name and
 * its context together.
 */
export async function invokeAmTpDemoAiTask<K extends TAmDemoAiTask>(
  task: K,
  context: TAmDemoAiContextFor<K>
): Promise<TAmDemoResult<TAmDemoAiInvokeResponseFor<K>>> {
  try {
    const response = await fetcher(AM_TP_DEMO_ROUTES.aiInvoke, {
      method: 'POST',
      body: { task, context },
    });

    if (!response.ok) {
      return { success: false, error: `Failed to run the ${task} step` };
    }

    const data = (await response.json()) as TAmDemoAiInvokeResponseFor<K>;

    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : `Failed to run the ${task} step`,
    };
  }
}
